import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminFiles,
  uploadAdminFile,
  deleteAdminFile,
  clearFileError,
} from "../../redux/slices/fileSlice";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Trash2, Upload, Loader2, ExternalLink } from "lucide-react";
import { useToast } from "../../components/ui/use-toast";
import { resolveAssetUrl } from "../../lib/asset-url";

function FilesPage() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { files, total, loading, error } = useSelector((s) => s.files);
  const fileInputRef = useRef(null);
  const [category, setCategory] = useState("content_media");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    dispatch(fetchAdminFiles({ limit: 100, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Files",
        description: error,
        variant: "destructive",
      });
      dispatch(clearFileError());
    }
  }, [error, toast, dispatch]);

  const openFile = (fileUrl) => {
    window.open(resolveAssetUrl(fileUrl), "_blank", "noopener,noreferrer");
  };

  const onUploadClick = () => fileInputRef.current?.click();

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await dispatch(uploadAdminFile({ file, category })).unwrap();
      toast({ title: "Uploaded" });
      dispatch(fetchAdminFiles({ limit: 100, offset: 0 }));
    } catch (err) {
      toast({
        title: "Upload failed",
        description: typeof err === "string" ? err : "Could not upload",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file record and remove from disk?")) return;
    try {
      await dispatch(deleteAdminFile(id)).unwrap();
      toast({ title: "Deleted" });
      dispatch(fetchAdminFiles({ limit: 100, offset: 0 }));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Delete failed",
        variant: "destructive",
      });
    }
  };

  const formatSize = (n) => {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Files</h1>
        <p className="text-muted-foreground">
          All uploads (admin scope). Links open on the API host.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload</CardTitle>
          <CardDescription>
            Category is sent as multipart field (same as mobile API).
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,application/pdf,video/mp4,.doc,.docx"
            onChange={onFileSelected}
          />
          <div className="grid gap-2">
            <span className="text-sm font-medium">Category</span>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="content_media">content_media</SelectItem>
                <SelectItem value="user_upload">user_upload</SelectItem>
                <SelectItem value="profile_avatar">profile_avatar</SelectItem>
                <SelectItem value="profile_cover">profile_cover</SelectItem>
                <SelectItem value="other">other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onUploadClick} disabled={uploading}>
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Choose file
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent uploads</CardTitle>
            <CardDescription>{total} file(s)</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => dispatch(fetchAdminFiles({ limit: 100, offset: 0 }))}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading && files.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Open</TableHead>
                  <TableHead className="text-right">Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="max-w-[200px] truncate text-sm">
                      {f.original_filename || f.filename}
                    </TableCell>
                    <TableCell>{f.category}</TableCell>
                    <TableCell>{f.file_type}</TableCell>
                    <TableCell>{formatSize(f.file_size)}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openFile(f.file_url)}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => remove(f.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && files.length === 0 && (
            <p className="text-muted-foreground text-center py-8">
              No files in storage.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FilesPage;
